// Cache tags for the generated course-catalogue endpoints (the global catalogue the CS team
// manages, distinct from any tenant-scoped course view), kept separate from the api wiring in
// `courseCatalogEnhanced.ts` so the policy can be read and tested without pulling in the whole
// store. Deliberately import-free.

const COURSES_LIST = { type: 'Courses' as const, id: 'LIST' }
const COURSE_VARIANTS_LIST = { type: 'CourseVariants' as const, id: 'LIST' }

// The catalogue endpoint answers paginated ({ items }) or as a plain array depending on its
// params; both shapes get per-row tags. The guards also keep error payloads out of the mapper.
const providesCoursesList = (result: { items?: { id: string }[] } | { id: string }[] | undefined) => {
  if (!result) return [COURSES_LIST]

  if ('items' in result && Array.isArray(result.items)) {
    return [...result.items.map(({ id }) => ({ type: 'Courses' as const, id })), COURSES_LIST]
  }

  if (Array.isArray(result)) {
    return [...result.map(({ id }) => ({ type: 'Courses' as const, id })), COURSES_LIST]
  }

  return [COURSES_LIST]
}

// Courses are addressed by code everywhere in the catalogue UI, so the per-course tag id is the
// code, taken from the request rather than the response, which a failed request does not have.
const courseByCode = (_result: unknown, _error: unknown, arg: { courseCode: string }) => [
  COURSES_LIST,
  { type: 'Courses' as const, id: arg.courseCode },
]

export const courseCatalogCacheTags = {
  listCourses: {
    providesTags: providesCoursesList,
  },
  getCourseByCode: {
    providesTags: (_result: unknown, _error: unknown, arg: { courseCode: string }) => [
      { type: 'Courses' as const, id: arg.courseCode },
    ],
  },
  createCourse: {
    invalidatesTags: [COURSES_LIST],
  },
  updateCourseByCode: {
    invalidatesTags: courseByCode,
  },
  deleteCourseByCode: {
    invalidatesTags: courseByCode,
  },
  listCourseVariants: {
    providesTags: (result: { id: string }[] | undefined) =>
      Array.isArray(result)
        ? [...result.map(({ id }) => ({ type: 'CourseVariants' as const, id })), COURSE_VARIANTS_LIST]
        : [COURSE_VARIANTS_LIST],
  },
  createCourseVariant: {
    invalidatesTags: [COURSE_VARIANTS_LIST],
  },
  deleteCourseVariant: {
    invalidatesTags: [COURSE_VARIANTS_LIST],
  },
  updateCourseVariant: {
    invalidatesTags: [COURSE_VARIANTS_LIST],
  },
  syncLmdhCourseVariants: {
    invalidatesTags: [COURSE_VARIANTS_LIST],
  },
} as const
